import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { doc, getDoc, collection, query, where, getDocs, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { getStorage, ref as storageFBRef, getDownloadURL } from 'firebase/storage';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import ShareButton from '../../components/ui/ShareButton';
import { SITE_URL, getProductShareUrl } from '../../constants/site';
import { Button, Container, Row, Col, Card, ListGroup, Spinner, Alert, Badge, Modal } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';
import { useNotification } from '../../components/ui/Notification';
import { formatCartItems, calculateTotalItems, calculateTotalPrice, getActiveCartInstanceId } from '../../utils/cartHelpers';

const logger = createLogger('ProductDetailsByProduct');

const ProductImage = ({ imagePath, altText, className, style }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState(false);
  const storage = getStorage();

  useEffect(() => {
    setImageUrl('');
    setError(false);
    if (imagePath && typeof imagePath === 'string' && !imagePath.startsWith('blob:') && !imagePath.startsWith('http')) {
      const imageRef = storageFBRef(storage, imagePath);
      getDownloadURL(imageRef)
        .then(setImageUrl)
        .catch(() => setError(true));
    } else if (imagePath && typeof imagePath === 'string' && imagePath.startsWith('http')) {
      setImageUrl(imagePath);
    } else if (imagePath) {
      setError(true);
    }
  }, [imagePath, storage]);

  const defaultStyle = { height: '200px', backgroundColor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  if (error) {
    return <div className={className || ''} style={{ ...defaultStyle, ...style }}><span>Image Error</span></div>;
  }
  if (!imageUrl) {
    return <div className={className || ''} style={{ ...defaultStyle, ...style }}><span>Loading...</span></div>;
  }
  return <img src={imageUrl} alt={altText} className={className || ''} style={style} onError={() => setError(true)} />;
};

/**
 * Product details page by product ID only.
 * Shows the product and all supermarkets where it can be bought.
 * Clicking a supermarket (or "Add to Cart") adds the item to the user's cart for that supermarket.
 */
const ProductDetailsByProduct = ({ productIdProp }) => {
  const { slug } = useParams();
  const effectiveProductId = productIdProp ?? slug;
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [supermarketsWithInventory, setSupermarketsWithInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorPage, setErrorPage] = useState(null);
  const [addingSupermarketId, setAddingSupermarketId] = useState(null);
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();

  const [showDealsModal, setShowDealsModal] = useState(false);
  const [selectedProductDeals, setSelectedProductDeals] = useState([]);
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedProductPrice, setSelectedProductPrice] = useState(0);
  const [selectedSupermarketName, setSelectedSupermarketName] = useState('');
  const [productImageUrl, setProductImageUrl] = useState('');

  const handleViewDeals = (item) => {
    const deals = item.discount_deals || [];
    setSelectedProductDeals(deals);
    setSelectedProductName(product?.display_name || 'Unknown Product');
    setSelectedProductPrice(item.sale_price != null ? Number(item.sale_price) : 0);
    setSelectedSupermarketName(item.supermarketName || '');
    setShowDealsModal(true);
  };

  const handleCloseDealsModal = () => {
    setShowDealsModal(false);
    setSelectedProductDeals([]);
    setSelectedProductName('');
    setSelectedProductPrice(0);
    setSelectedSupermarketName('');
  };

  const breadcrumbItems = product
    ? [
        { path: '/', label: 'Home' },
        { path: '/products', label: 'Products' },
        { label: product.display_name || 'Product Details' }
      ]
    : [{ path: '/', label: 'Home' }];

  const handleAddToCart = useCallback(
    async (supermarketId, supermarketName, inventoryDetails) => {
      if (!product || !inventoryDetails || !currentUser) {
        alert('Please log in to add items to your cart.');
        navigate(`/login?path=${encodeURIComponent(window.location.pathname)}`);
        return;
      }
      if (inventoryDetails.quantity <= 0) {
        alert('This product is out of stock at this supermarket.');
        return;
      }
      const userId = currentUser.uid;
      const cartRef = doc(db, 'Carts', userId);
      setAddingSupermarketId(supermarketId);
      try {
        let updatedCartData = null;
        await runTransaction(db, async (transaction) => {
          const cartDoc = await transaction.get(cartRef);
          const cartData = cartDoc.exists() ? cartDoc.data() : {};

          let supermarketCartInstances = cartData[supermarketId] || {};
          if (
            typeof supermarketCartInstances !== 'object' ||
            Array.isArray(supermarketCartInstances) ||
            supermarketCartInstances === null
          ) {
            supermarketCartInstances = {};
          }
          let activeCartInstanceId = null;
          let activeCartInstance = null;
          for (const instanceId in supermarketCartInstances) {
            if (
              supermarketCartInstances.hasOwnProperty(instanceId) &&
              supermarketCartInstances[instanceId] &&
              supermarketCartInstances[instanceId].status === 'active'
            ) {
              activeCartInstanceId = instanceId;
              activeCartInstance = supermarketCartInstances[instanceId];
              break;
            }
          }
          const newProductEntry = {
            productId: effectiveProductId,
            quantity: 1,
            name: product.display_name,
            price: inventoryDetails.sale_price
          };
          if (activeCartInstance) {
            const productIndex = activeCartInstance.products.findIndex((p) => p.productId === effectiveProductId);
            if (productIndex > -1) activeCartInstance.products[productIndex].quantity += 1;
            else activeCartInstance.products.push(newProductEntry);
            activeCartInstance.lastUpdatedAt = serverTimestamp();
            activeCartInstance.hasUnseenUpdate = true;
          } else {
            activeCartInstanceId = `cart_instance_${Date.now()}`;
            activeCartInstance = {
              instanceId: activeCartInstanceId,
              products: [newProductEntry],
              status: 'active',
              supermarketName,
              createdAt: serverTimestamp(),
              lastUpdatedAt: serverTimestamp(),
              hasUnseenUpdate: true
            };
          }
          supermarketCartInstances[activeCartInstanceId] = activeCartInstance;
          const updateData = {
            ...cartData,
            [supermarketId]: supermarketCartInstances,
            lastGlobalUpdate: serverTimestamp()
          };
          if (!cartDoc.exists()) transaction.set(cartRef, updateData);
          else transaction.update(cartRef, updateData);

          updatedCartData = { [activeCartInstanceId]: activeCartInstance };
        });

        const addedItem = {
          name: product.display_name,
          quantity: 1,
          price: inventoryDetails.sale_price
        };

        const activeCartInstanceId = getActiveCartInstanceId(updatedCartData || {});

        showNotification({
          type: 'cart',
          title: 'Item Added to Cart',
          message: '',
          duration: 6000,
          cartData: {
            addedItem,
            cartItems: formatCartItems(updatedCartData || {}),
            totalItems: calculateTotalItems(updatedCartData || {}),
            totalPrice: calculateTotalPrice(updatedCartData || {}),
            supermarketName
          },
          onViewCart: () => navigate('/carts'),
          onCheckout: () => {
            if (activeCartInstanceId) {
              navigate(`/checkout/${supermarketId}/${activeCartInstanceId}`);
            } else {
              navigate(`/checkout/${supermarketId}`);
            }
          },
          userId: currentUser.uid,
          supermarketId
        });
      } catch (error) {
        logger.error('Error adding to cart:', error);
        alert(`Failed to add product to cart. ${error.message}`);
      } finally {
        setAddingSupermarketId(null);
      }
    },
    [currentUser, product, effectiveProductId, navigate, showNotification]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!effectiveProductId) {
        setErrorPage('Missing product ID.');
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const productRef = doc(db, 'Products', effectiveProductId);
        const productSnap = await getDoc(productRef);
        if (!productSnap.exists()) {
          setErrorPage('Product not found.');
          setLoading(false);
          return;
        }
        const fetchedProduct = { id: productSnap.id, ...productSnap.data() };
        setProduct(fetchedProduct);

        const inventoryQuery = query(
          collection(db, 'Inventory'),
          where('product_id', '==', effectiveProductId),
          where('quantity', '>', 0)
        );
        const inventorySnapshot = await getDocs(inventoryQuery);
        if (inventorySnapshot.empty) {
          setSupermarketsWithInventory([]);
          setLoading(false);
          return;
        }

        const supermarketIds = [...new Set(inventorySnapshot.docs.map((d) => d.data().supermarket_id))];
        const supermarketPromises = supermarketIds.map(async (smId) => {
          const supermarketSnap = await getDoc(doc(db, 'Supermarkets', smId));
          const inventoryDoc = inventorySnapshot.docs.find((d) => d.data().supermarket_id === smId);
          const inventoryData = inventoryDoc ? inventoryDoc.data() : {};
          return {
            supermarketId: smId,
            supermarketName: supermarketSnap.exists() ? supermarketSnap.data().display_name || 'Unknown Supermarket' : 'Unknown Supermarket',
            ...inventoryData
          };
        });
        const list = await Promise.all(supermarketPromises);
        setSupermarketsWithInventory(list);
      } catch (err) {
        logger.error('Error fetching product/supermarkets:', err);
        setErrorPage('Failed to load product details.');
      }
      setLoading(false);
    };
    fetchData();
  }, [effectiveProductId]);

  // Fetch product image URL for SEO/social sharing
  useEffect(() => {
    if (!product?.media?.[0]?.url || product.media[0].url.startsWith('http')) return;
    const storage = getStorage();
    const imageRef = storageFBRef(storage, product.media[0].url);
    getDownloadURL(imageRef)
      .then(setProductImageUrl)
      .catch(() => setProductImageUrl(''));
  }, [product?.media]);

  if (loading) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <Spinner animation="border" />
          <p>Loading details...</p>
        </Container>
      </Layout>
    );
  }
  if (errorPage) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <p style={{ color: 'red' }}>{errorPage}</p>
          <Button variant="outline-primary" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Container>
      </Layout>
    );
  }
  if (!product) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <p>Product not found.</p>
          <Button variant="outline-primary" onClick={() => navigate('/')}>
            Back to Home
          </Button>
        </Container>
      </Layout>
    );
  }

  const { display_name, description, media, attributes } = product;

  const seoTitle = `${display_name} | Pack My Cart`;
  const seoDescription = description || `${display_name} - Compare prices and shop at multiple supermarkets.`;
  const seoUrl = getProductShareUrl(product);
  const seoImage = productImageUrl || `${SITE_URL}/logo192.png`;

  return (
    <Layout>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="Pack My Cart" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: display_name,
            description: description || seoDescription,
            image: seoImage,
            url: seoUrl,
          })}
        </script>
      </Helmet>
      <Container className="mt-4" style={{ maxWidth: '900px' }}>
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
              &larr; Go Back
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h1 className="mb-0">{display_name || 'Product Name Not Available'}</h1>
          <ShareButton
            url={seoUrl}
            title={seoTitle}
            text={seoDescription}
            variant="outline-secondary"
            size="sm"
          />
        </div>

        <Row className="mb-4">
          <Col md={5} className="mb-3 mb-md-0">
            {media && media.length > 0 && media[0]?.url ? (
              <ProductImage
                imagePath={media[0].url}
                altText={display_name || 'Main product image'}
                style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', border: '1px solid #eee', borderRadius: '4px' }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '300px',
                  backgroundColor: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px'
                }}
              >
                <span>No Image Available</span>
              </div>
            )}
          </Col>
          <Col md={7}>
            {description && <p className="text-muted mb-4">{description}</p>}
            {attributes && attributes.length > 0 ? (
              <Card className="mb-0">
                <Card.Header>
                  <h5 className="mb-0">Product Attributes</h5>
                </Card.Header>
                <ListGroup variant="flush">
                  {attributes.map((attr, index) => (
                    <ListGroup.Item key={index}>
                      <strong>{attr.name || 'N/A'}:</strong> {attr.value || 'N/A'}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card>
            ) : null}
          </Col>
        </Row>

        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">Where to buy</h5>
          </Card.Header>
          <ListGroup variant="flush">
            {supermarketsWithInventory.length === 0 ? (
              <ListGroup.Item>No supermarkets currently have this product in stock.</ListGroup.Item>
            ) : (
              supermarketsWithInventory.map((item) => (
                <ListGroup.Item
                  as="div"
                  key={item.supermarketId}
                  action
                  className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between py-3 gap-2"
                  onClick={() =>
                    currentUser && item.quantity > 0 && addingSupermarketId === null
                      ? handleAddToCart(item.supermarketId, item.supermarketName, item)
                      : undefined
                  }
                  style={{ cursor: currentUser && item.quantity > 0 ? 'pointer' : 'default' }}
                >
                  <div className="flex-grow-1">
                    <Link
                      to={`/supermarkets/${item.supermarketId}/products`}
                      className="fw-semibold text-decoration-none"
                      style={{ color: '#1a7f37' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {item.supermarketName}
                    </Link>
                    <div className="small text-muted mt-1">
                      ${item.sale_price != null ? Number(item.sale_price).toFixed(2) : 'N/A'} &middot; Stock: {item.quantity != null ? item.quantity : 'N/A'}
                    </div>
                    {item.discount_deals && item.discount_deals.length > 0 && (
                      <>
                        <Badge bg="success" className="mt-1 me-1">
                          {item.discount_deals.length} Deal{item.discount_deals.length !== 1 ? 's' : ''}
                        </Badge>
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="mt-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDeals(item);
                          }}
                        >
                          View Deals
                        </Button>
                      </>
                    )}
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    className="align-self-sm-center"
                    disabled={!currentUser || item.quantity <= 0 || addingSupermarketId !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCart(item.supermarketId, item.supermarketName, item);
                    }}
                  >
                    {addingSupermarketId === item.supermarketId ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                        Adding...
                      </>
                    ) : !currentUser ? (
                      'Login to Add'
                    ) : item.quantity <= 0 ? (
                      'Out of Stock'
                    ) : (
                      'Add to Cart'
                    )}
                  </Button>
                </ListGroup.Item>
              ))
            )}
          </ListGroup>
        </Card>
      </Container>

      <Modal show={showDealsModal} onHide={handleCloseDealsModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Discount Deals for {selectedProductName}
            {selectedSupermarketName ? ` at ${selectedSupermarketName}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedProductDeals.length === 0 ? (
            <p>No discount deals currently available for this item.</p>
          ) : (
            <div>
              <p className="text-muted mb-3">
                <strong>Regular Price:</strong> ${selectedProductPrice.toFixed(2)} per item
              </p>
              <ListGroup>
                {selectedProductDeals.map((deal, index) => {
                  const regularPrice = selectedProductPrice * deal.quantity;
                  const savings = regularPrice - deal.price;
                  const savingsPercentage = ((savings / regularPrice) * 100).toFixed(1);
                  return (
                    <ListGroup.Item key={index} className="d-flex justify-content-between align-items-center">
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <strong className="fs-5">{deal.quantity} items</strong>
                            <br />
                            <span className="text-success fw-bold fs-4">${deal.price.toFixed(2)}</span>
                          </div>
                          <Badge bg="success" className="fs-6">
                            Save {savingsPercentage}%
                          </Badge>
                        </div>
                        <div className="text-muted small">
                          <div>Regular price: ${regularPrice.toFixed(2)}</div>
                          <div>You save: <span className="text-success fw-bold">${savings.toFixed(2)}</span></div>
                          <div>Per item: <span className="text-success fw-bold">${(deal.price / deal.quantity).toFixed(2)}</span></div>
                        </div>
                      </div>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
              <div className="mt-3 p-3 bg-light rounded">
                <small className="text-muted">
                  <i className="fas fa-info-circle me-1"></i>
                  These deals are automatically applied when you add the specified quantity to your cart.
                </small>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDealsModal}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default ProductDetailsByProduct;
